import {
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type Ref,
  useState,
} from 'react';
import { Input } from '@/components/ui/input';
import { formatNumber } from '@/utils/formatters';
import { cn } from '@/lib/utils';

interface NumberInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'ref' | 'min' | 'max'
> {
  value: number | null;
  onChange: (value: number | null) => void;
  /** Clamp value to a minimum. */
  min?: number;
  /** Clamp value to a maximum. */
  max?: number;
  allowNegative?: boolean;
  thousandSeparator?: boolean;
  /** Max decimal digits. Set to 0 for integer-only input. @default 5 */
  decimalPlaces?: number;
  selectAllOnFocus?: boolean;
  prefix?: string;
  suffix?: string;
  ref?: Ref<HTMLInputElement>;
}

function formatRaw(raw: string, thousandSeparator: boolean): string {
  if (!thousandSeparator) return raw;
  const isNeg = raw.startsWith('-');
  const body = isNeg ? raw.slice(1) : raw;
  if (body === '' || body === '.') return raw;

  const dotIdx = body.indexOf('.');
  const intPart = dotIdx === -1 ? body : body.slice(0, dotIdx);
  const fracPart = dotIdx === -1 ? '' : body.slice(dotIdx);
  const intFormatted =
    intPart === '' ? '' : Number.parseInt(intPart, 10).toLocaleString('en-US');
  return (isNeg ? '-' : '') + intFormatted + fracPart;
}

function parseRaw(raw: string): number | null {
  if (raw === '' || raw === '-' || raw === '.' || raw === '-.') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function sanitizeBody(body: string, decimalPlaces: number): string {
  if (decimalPlaces <= 0) return body.replace(/\D/g, '');

  let cleanBody = body.replace(/[^\d.]/g, '');
  const firstDot = cleanBody.indexOf('.');
  if (firstDot !== -1) {
    cleanBody =
      cleanBody.slice(0, firstDot + 1) +
      cleanBody.slice(firstDot + 1).replaceAll('.', '');
  }
  const [intPart, fracPart = ''] = cleanBody.split('.');
  if (fracPart.length > decimalPlaces) {
    cleanBody = `${intPart}.${fracPart.slice(0, decimalPlaces)}`;
  }
  return cleanBody;
}

export function NumberInput({
  value,
  onChange,
  min,
  max,
  allowNegative = false,
  thousandSeparator = true,
  decimalPlaces = 5,
  selectAllOnFocus = true,
  prefix,
  suffix,
  className,
  onBlur,
  ref,
  ...props
}: NumberInputProps) {
  const allowDecimals = decimalPlaces > 0;
  const [editingRaw, setEditingRaw] = useState<string | null>(null);

  const displayValue = (() => {
    if (editingRaw !== null) return formatRaw(editingRaw, thousandSeparator);
    if (value === null) return '';
    return formatNumber(value, {
      thousandSeparator,
      maximumFractionDigits: decimalPlaces,
      truncate: !allowDecimals,
    });
  })();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const stripped = e.target.value.replaceAll(',', '');

    if (stripped === '') {
      setEditingRaw('');
      onChange(null);
      return;
    }

    if (allowNegative && stripped === '-') {
      setEditingRaw('-');
      onChange(null);
      return;
    }

    const isNeg = allowNegative && stripped.startsWith('-');
    const withoutSign = isNeg ? stripped.slice(1) : stripped;

    const raw = (isNeg ? '-' : '') + sanitizeBody(withoutSign, decimalPlaces);
    const parsed = parseRaw(raw);

    if (parsed !== null && max !== undefined && parsed > max) {
      const clamped = max;
      setEditingRaw(String(clamped));
      onChange(clamped);
      return;
    }
    if (parsed !== null && min !== undefined && parsed < min) {
      const clamped = min;
      setEditingRaw(String(clamped));
      onChange(clamped);
      return;
    }

    setEditingRaw(raw);
    onChange(parsed);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setEditingRaw(null);
    onBlur?.(e);
  };

  const inputElement = (
    <Input
      ref={ref}
      type="text"
      inputMode={allowDecimals ? 'decimal' : 'numeric'}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={cn(prefix ? 'pl-8' : '', suffix ? 'pr-8' : '', className)}
      selectAllOnFocus={selectAllOnFocus}
      {...props}
    />
  );

  if (!prefix && !suffix) return inputElement;

  return (
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
          {prefix}
        </span>
      )}
      {inputElement}
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  );
}
